import React from 'react';
import { NextPage } from 'next';
import {
  Grid, SpacingName, Button, BreakpointQueryName,
} from '@doveit/lego';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import BottomBar, { BottomBarProps } from '../client/templates/common/bottom-bar/BottomBar';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import buildBreadcrumb from '../client/utils/breadcrumb/evaluation';
import { City, HeroSection, TextAlign } from '../client/types';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import Hero from '../client/templates/common/hero/Hero';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { getEvaluationStructuredData } from '../data/structured/evaluation';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { buildNotFound } from '../client/utils/error/http/httpErrors';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { EVALUATIONS } from '../client/graphql/queries/evaluations';
import InfoBlock from '../client/components/info-block/InfoBlock';
import PageSection from '../client/components/page-section/PageSection';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { buildEvaluationByLocalityURLObject, slugIt } from '../client/utils/links';
import * as styles from '../client/templates/evaluation/Evaluation.style';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import CityList from '../client/components/city-list/CityList';
import { CitiesSectionComponent, EvaluationListing, GenericSectionComponent } from '../domain/graphcms';
import { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { importDynamicData } from '../services/importDynamicData';

type SectionComponent = GenericSectionComponent | CitiesSectionComponent;

type EvaluationListingGraphQLResponse = { evaluationListings: EvaluationListing[] };

export interface EvaluationProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  infoSection: InfoSectionProps,
  cities: City[],
  sections: SectionComponent[],
  bottomBar: BottomBarProps,
}

export const Evaluation: NextPage<EvaluationProps> = ({
  SEO,
  hero,
  infoSection,
  sections,
  cities,
  bottomBar,
}) => {
  const { current, ref: formRef, isVisible: isFormVisible } = useRefInView({
    threshold: 0,
  });

  const anchors = React.useMemo(() => (
    // remove always the first article block that serves as index
    sections.slice(1)
      .map((block) => (
        block.__typename === 'CitiesSectionComponent'
          ? (block as CitiesSectionComponent).citiesSectionTitle
          : (block as GenericSectionComponent).title.markdown))
  ), [sections]);

  const breadcrumb = buildBreadcrumb();

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
          getEvaluationStructuredData(),
        )}
      />

      <Hero
        title={hero.title}
        description={hero.description}
        secondarySlotTitle={hero.formTitle}
        backgroundImage={hero.backgroundImage}
        secondarySlot={(
          <div ref={formRef}>
            <CreateEvaluation
              onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
            />
          </div>
        )}
      />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {infoSection.title}
          </PageSection.Title>
          {infoSection.description && (
            <PageSection.Description text={infoSection.description} />
          )}
          <PageSection.Block>
            <Grid gutter={SpacingName.SMALLER}>
              {infoSection.reasons.map((reason) => (
                <Grid.Unit
                  size={{ XS: 1, MD: 1 / infoSection.reasons.length }}
                  key={reason.title}
                >
                  <InfoBlock
                    image={{
                      src: requireFromStaticAsset(reason.picture),
                      alt: reason.alt ?? `Icona ${infoSection.title}`,
                    }}
                    title={reason.title}
                    description={reason.description}
                  />
                </Grid.Unit>
              ))}
            </Grid>
          </PageSection.Block>
          <PageSection.Actions
            textAlign={TextAlign.CENTER}
          >
            <Button
              label="Valutazione gratuita"
              onClick={scrollToRef(current)}
            />
          </PageSection.Actions>
        </PageSection.Wrapper>
      </PageSection>

      {
        sections.map((section, index) => {
          if (section.__typename === 'CitiesSectionComponent') {
            const { citiesSectionContent, citiesSectionTitle } = section as CitiesSectionComponent;

            return (
              <>
                <div
                  id={slugIt(citiesSectionTitle)}
                  css={styles.anchor}
                />
                <SectionDivider />
                <PageSection>
                  <PageSection.Wrapper>
                    <PageSection.Title textAlign={TextAlign.CENTER}>
                      {citiesSectionTitle}
                    </PageSection.Title>
                    <PageSection.Description
                      text={citiesSectionContent}
                      textAlign={TextAlign.CENTER}
                    />
                    <PageSection.Block>
                      <CityList
                        cities={cities.map((item) => ({
                          name: item.title,
                          picture: item.smallPicture,
                          averageSellPrice: item.averageSellPrice,
                          title: `Valutazione immobile a ${item.title}`,
                          urlObject: buildEvaluationByLocalityURLObject({ locality: item.locality }),
                        }))}
                      />
                    </PageSection.Block>
                  </PageSection.Wrapper>
                </PageSection>
              </>
            );
          }

          const { title, content } = section as GenericSectionComponent;
          return (
            <>
              <div
                id={slugIt(title.markdown)}
                css={styles.anchor}
              />
              <PageSection>
                <PageSection.Wrapper>
                  <PageSection.Title textAlign={TextAlign.CENTER}>
                    {title.markdown}
                  </PageSection.Title>
                  <PageSection.Block>
                    <ArticleMarkdown>
                      {content.markdown}
                    </ArticleMarkdown>
                    {index === 0 && anchors.length > 0 && (
                      <ul css={styles.anchorList}>
                        {anchors.map((anchor) => (
                          <li
                            css={styles.anchorListItem}
                            key={anchor}
                          >
                            <a href={`#${slugIt(anchor)}`}>
                              {anchor}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </PageSection.Block>
                  {index === sections.length - 1 && (
                    <PageSection.Actions
                      textAlign={TextAlign.CENTER}
                    >
                      <Button
                        label="Quanto vale la mia casa?"
                        onClick={scrollToRef(current)}
                      />
                    </PageSection.Actions>
                  )}
                </PageSection.Wrapper>
              </PageSection>
            </>
          );
        })
      }

      <PageLayout.Wrapper>
        <PageLayout.Breadcrumb items={breadcrumb} />
      </PageLayout.Wrapper>

      <BottomBar
        hiddenFrom={BreakpointQueryName.MD}
        expanded={!isFormVisible}
        label={bottomBar.label}
        title={bottomBar.title}
        headline={bottomBar.headline}
        onClick={scrollToRef(current)}
      />
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<EvaluationProps>(async () => {
  const data = await importDynamicData('pages/evaluation.yml');

  const graphQLClient = initGraphQLClient();
  const { data: { evaluationListings: [evaluations] } } = await graphQLClient.query<EvaluationListingGraphQLResponse>({
    query: EVALUATIONS,
  });

  assertNotNullOrUndefined(evaluations, buildNotFound('Evaluation not found'));

  const cities = evaluations.evaluations
    .sort((a, b) => a.locality.name.localeCompare(b.locality.name))
    .map((item) => ({
      title: item.locality.name,
      locality: item.locality.slug,
      picture: item.locality?.coverPreviewVertical?.url || requireFromStaticAsset('cities/default-city.png'),
      smallPicture: item.locality?.coverPreviewHorizontal?.url || requireFromStaticAsset('cities/small/default-city.png'),
      averageSellPrice: item.locality.averageSellPrice,
    }));

  return {
    props: {
      ...data,
      SEO: {
        ...data.SEO,
        ...evaluations.seo,
      },
      hero: {
        title: evaluations.hero.title.markdown.trim(),
        description: evaluations.hero.content.markdown.trim(),
        backgroundImage: {
          SMALL: evaluations.hero.mobileImage?.url || null,
          LARGE: evaluations.hero.desktopImage?.url || null,
        },
        imageAlt: evaluations.hero.imageAlt || null,
      },
      infoSection: {
        title: data.infoSection.title,
        reasons: data.infoSection.reasons.map((reason) => ({
          title: reason.title,
          description: reason.description,
          picture: reason.picture,
          alt: reason.alt,
        })),
      },
      bottomBar: {
        label: data.bottomBar.label,
        title: data.bottomBar.title,
      },
      sections: evaluations.sections,
      cities,
    },
  };
});

export default withError(Evaluation);
